<template>
  <div v-if="isVisible" class="modal-overlay" @click.self="closeModal">
    <div class="modal-content">
      <button class="close-btn" @click="closeModal">&times;</button>
      <img :src="corporateLogo ? corporateLogo : require('@/../public/img/Color-Alternate.png')" alt="Corporate Logo"
        class="corporate-logo" />
      <h3 class="modal-title">Complete Your Profile</h3>
      <p class="modal-subtitle">
        Your phone number and country are incomplete. <br> Please add them to continue or update your personal profile
        <router-link to="/client/account" style="color: var(--primary-color, #F93E3E); font-weight: 700;">Here</router-link>
      </p>

      <form @submit.prevent="handleSubmit">
        <div class="form-group">
          <label>Country <span class="text-danger">*</span></label>
          <v-select
            v-model="user.country"
            :options="formattedCountries"
            :reduce="option => option.value"
            label="text"
            placeholder="-- Search & Select Country --"
            class="modern-v-select"
            :clearable="false"
          ></v-select>
        </div>

        <div class="form-group">
          <label>Phone <span class="text-danger">*</span></label>
          <div class="d-flex gap-2 align-items-center">
            <span v-if="user.country && !unknownCountry && countryCode" class="country-code-badge">{{ countryCode }}</span>
            <div style="flex: 1;">
              <o-input-text name="phone" :autocomplete="'off'" v-model="user.phone" type="text" :required="true"
                :numbers-only="true" />
            </div>
          </div>
        </div>

        <button type="submit" class="btn btn-primary btn-submit">
          Save Number
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { OInputText } from '@/components/OmnihControl'; 
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { ref, reactive, watch, onBeforeUnmount, computed } from 'vue';

export default {
  name: "OProfileCompletionModal",
  components: {
    vSelect,
    OInputText,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    countries: {
      type: Array,
      required: true
    }
  },
  setup(props, { emit }) {
    const corporateLogo = ref(null);
    const countryCode = ref('');
    const unknownCountry = ref(false);
    const user = reactive({
      phone: "",
      country: null,
    });

    corporateLogo.value = localStorage.getItem('corporateLogo');

    const updatePhoneCode = () => {
      if (!user.country) {
        unknownCountry.value = true;
        return;
      }

      // Find country data by ID and get phone_code directly
      const country = props.countries.find(c => Number(c.value) === Number(user.country));

      if (country && country.phone_code) {
        unknownCountry.value = false;
        countryCode.value = `${country.phone_code}`;
      } else {
        unknownCountry.value = true;
        countryCode.value = '';
        console.error(`Phone code not found for country ID: ${user.country}`);
      }
    }

    watch(() => user.country, updatePhoneCode);

    watch(() => props.isVisible, (newVal) => {
      if (newVal) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }, { immediate: true });

    onBeforeUnmount(() => {
      document.body.style.overflow = '';
    });

    const closeModal = () => {
      user.phone = "";
      user.country = null;
      countryCode.value = '';
      emit("close");
    };

    const handleSubmit = () => {
      if (!user.country) {
        alert("Please select your country.");
        return;
      }
      if (user.phone.length < 6) { 
        alert("Please enter a valid phone number.");
        return;
      }
   
      const finalPhone = countryCode.value + user.phone;
      
      emit("submit-phone", { 
        phone: finalPhone,
        country: Number(user.country) 
      });
    };

    const formattedCountries = computed(() => {
      return props.countries.map(c => ({
        value: Number(c.value),
        text: `${c.text} (${c.phone_code ? c.phone_code : '-'})`
      }));
    });

    return {
      corporateLogo,
      countryCode,
      unknownCountry,
      user,
      closeModal,
      handleSubmit,
      formattedCountries,
    };
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  overflow-y: auto;
  z-index: 9999;
  padding: 20px 10px;
  box-sizing: border-box;
}

@media (max-height: 600px) {
  .modal-overlay {
    align-items: flex-start;
  }
}

.modal-content {
  margin: auto;
  position: relative;
  width: 90%;
  max-width: 450px;
  background: #ffffff;
  border-radius: 12px;
  padding: 40px 30px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  text-align: center;
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 15px;
  background: none;
  border: none;
  font-size: 2.5rem;
  color: #aaa;
  cursor: pointer;
  line-height: 1;
}

.close-btn:hover {
  color: #333;
}

.corporate-logo {
  display: block;
  margin-left: auto;
  margin-right: auto;
  max-width: 200px;
  max-height: 80px;
  height: auto;
  margin-bottom: 20px;
  object-fit: contain;
}

.modal-title {
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 10px;
}

.modal-subtitle {
  font-family: "Poppins", sans-serif;
  font-weight: 400;
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 25px;
}

.form-group {
  text-align: left;
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 0.9rem;
  color: #444;
  margin-bottom: 8px;
}

.text-danger {
  color: var(--primary-color, #f93e3e);
  margin-left: 2px;
}

:deep(.input-wrapper) {
  margin: 0 !important;
}

:deep(.form-control),
.form-control {
  width: 100%;
  background-color: #f8fafc !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
  padding: 10px 14px !important;
  height: 44px !important;
  font-size: 14px !important;
  color: #334155 !important;
  margin: 0 !important;
  transition: all 0.2s ease !important;
  box-sizing: border-box !important;
}

:deep(.form-control:focus),
.form-control:focus {
  background-color: #ffffff !important;
  border-color: var(--primary-color, #f93e3e) !important;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06) !important;
  outline: none !important;
}

.btn-submit {
  width: 100%;
  padding: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 8px;
}

.btn-primary {
  color: #fff;
  background-color: var(--primary-color, #f93e3e);
  border-color: var(--primary-border, #f93e3e);
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-primary:hover {
  filter: brightness(0.9);
}

:deep(.modern-v-select) {
  --vs-border-color: #cbd5e1;
  --vs-border-radius: 8px;
  --vs-font-size: 14px;
  --vs-colors--light: #64748b;
  text-align: left;
}

:deep(.modern-v-select .vs__dropdown-toggle) {
  background-color: #f8fafc;
  border: 1px solid var(--vs-border-color);
  border-radius: var(--vs-border-radius);
  padding: 6px 4px;
  min-height: 42px;
  margin: 0 !important;
  transition: all 0.2s ease;
}

:deep(.modern-v-select.vs--open .vs__dropdown-toggle),
:deep(.modern-v-select .vs__dropdown-toggle:focus-within) {
  background-color: #ffffff;
  border-color: var(--primary-color, #f93e3e);
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06);
}

:deep(.modern-v-select .vs__search::placeholder),
:deep(.modern-v-select .vs__dropdown-toggle),
:deep(.modern-v-select .vs__dropdown-menu) {
  font-family: inherit;
  font-size: 14px;
  color: #334155;
}

:deep(.modern-v-select .vs__clear),
:deep(.modern-v-select .vs__open-indicator) {
  fill: #94a3b8;
}

:deep(.modern-v-select .vs__dropdown-menu) {
  border-color: #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  margin-top: 4px;
  padding: 4px 0;
  max-height: 200px;
}

@media (max-height: 500px) {
  :deep(.modern-v-select .vs__dropdown-menu) {
    max-height: 120px !important;
  }
}

:deep(.modern-v-select .vs__dropdown-option) {
  padding: 8px 16px;
  color: #334155;
}

:deep(.modern-v-select .vs__dropdown-option--highlight) {
  background: var(--primary-color, #f93e3e);
  color: #ffffff;
}

/* Country Code Badge styling matching MemberForm */
.country-code-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: var(--primary-color, #F7A3A7);
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  padding: 0 12px;
  height: 44px;
  border-radius: 8px;
  white-space: nowrap;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

/* Allow vs__selected to take full width when closed, preventing 8-10 char squishing */
:deep(.modern-v-select .vs__selected-options) {
  flex-wrap: nowrap !important;
  overflow: hidden;
  align-items: center;
}

:deep(.modern-v-select .vs__selected) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  flex: 1 1 auto;
  min-width: 0;
  margin: 0 !important;
  padding: 0 6px !important;
  align-self: center;
  color: #334155;
  font-size: 14px;
}

:deep(.modern-v-select .vs__search),
:deep(.modern-v-select .vs__search:focus) {
  border: none !important;
  background-color: transparent !important;
  box-shadow: none !important;
  font-size: 14px !important;
  color: #0f172a !important;
  outline: none !important;
  margin: 0 !important;
  padding: 0 !important;
  width: 0 !important;
  min-width: 0 !important;
  flex: 0 0 auto !important;
}

:deep(.modern-v-select.vs--open .vs__search),
:deep(.modern-v-select.vs--searching .vs__search) {
  flex: 1 1 auto !important;
  width: auto !important;
  min-width: 60px !important;
  padding: 4px 6px !important;
}

:deep(.modern-v-select.vs--searching .vs__selected) {
  display: none !important;
}
</style>